// Shared presentational primitives for the comms pages.
// Extracted verbatim from CommsPage.jsx so pressAssets.jsx (and future
// comms modules) can reuse them without importing the 5k-line page file.
import React, { useState, useEffect, useRef } from 'react';
import { Link2, ExternalLink, Pencil, Lock, Unlock, RotateCcw, Plus, Trash2 } from 'lucide-react';

export const GOLD = '#9F902A';

export const S = {
  page:   { fontFamily: "'IBM Plex Sans', Raleway, sans-serif", minHeight: '100vh', backgroundColor: '#EAE8E1', padding: '32px 28px 90px' },
  card:   { backgroundColor: '#F7F6F2', border: '1px solid rgba(27,27,25,.12)', borderRadius: 4, padding: '18px 20px', marginBottom: 14 },
  h1:     { fontFamily: "'Spectral', Georgia, serif", fontWeight: 500, fontSize: 32, color: '#1B1B19', margin: 0 },
  h2:     { fontFamily: "'Spectral', Georgia, serif", fontWeight: 500, fontSize: 20, color: '#1B1B19', margin: '0 0 4px' },
  h3:     { fontFamily: "'Spectral', Georgia, serif", fontWeight: 500, fontSize: 18, color: '#1B1B19', margin: '0 0 2px' },
  eyebrow:{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6E6C66' },
  meta:   { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: '#6E6C66', margin: 0 },
  body:   { fontSize: 14, color: '#6E6C66', lineHeight: 1.6, margin: 0 },
  blockTitle: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#21302A', paddingBottom: 8, borderBottom: '1px solid #33473F', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  noteArea: { width: '100%', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13.5, lineHeight: 1.6, color: '#1B1B19', backgroundColor: '#FBFAF7', border: '1px solid rgba(27,27,25,.12)', borderRadius: 3, padding: '10px 12px', resize: 'vertical', minHeight: 48, boxSizing: 'border-box' },
  inputStyle: { width: '100%', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, backgroundColor: '#F7F6F2', border: '1px solid rgba(27,27,25,.12)', borderRadius: 3, padding: '7px 10px', boxSizing: 'border-box' },
  addLine: { display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' },
  filterRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 },
  grid2:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 14 },
};

export function statusClass(s) {
  if (!s) return 'line';
  const sl = s.toLowerCase();
  if (['published','active','running','won','entered','done','strong fit','priority target'].some(x => sl.includes(x))) return 'pub';
  if (['in pipeline','approach','enter','plan','in progress','submitted','shortlisted'].some(x => sl.includes(x))) return 'pipe';
  if (['watch','potential','considering'].some(x => sl.includes(x))) return 'pot';
  return 'new';
}

const CHIP_COLORS = {
  pub:  { backgroundColor: '#E0EAE3', color: '#2F5E4E' },
  pipe: { backgroundColor: '#EFE7D2', color: '#8A6A2A' },
  pot:  { backgroundColor: '#DEE7ED', color: '#3A566B' },
  new:  { backgroundColor: '#E6E4DD', color: '#76746D' },
  line: { backgroundColor: 'transparent', color: '#6E6C66', border: '1px solid rgba(27,27,25,.12)' },
  act:  { backgroundColor: 'transparent', color: '#21302A', border: '1px solid #33473F' },
};

export function Chip({ label, cls = 'line', onClick }) {
  const c = CHIP_COLORS[cls] || CHIP_COLORS.line;
  return (
    <span onClick={onClick} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 2, whiteSpace: 'nowrap', cursor: onClick ? 'pointer' : 'default', ...c }}>
      {label}
    </span>
  );
}

export function FilterBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', background: active ? '#DDE5DF' : 'none', border: active ? '1px solid #33473F' : '1px solid rgba(27,27,25,.12)', color: active ? '#21302A' : '#6E6C66', padding: '6px 11px', borderRadius: 2, cursor: 'pointer' }}>
      {label}
    </button>
  );
}

export function BlockTitle({ left, right }) {
  return (
    <div style={S.blockTitle}>
      <span>{left}</span>
      {right && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6E6C66', fontWeight: 400 }}>{right}</span>}
    </div>
  );
}

export function NoteArea({ value, onChange, onBlurSave, placeholder, minHeight = 48, saveOnBlur = false }) {
  const [local, setLocal] = useState(value || '');
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setLocal(value || ''); }, [value]);
  if (onBlurSave) {
    return <textarea
      value={local}
      onChange={e => { setLocal(e.target.value); onChange(e.target.value); }}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; onBlurSave(); }}
      placeholder={placeholder}
      style={{ ...S.noteArea, minHeight }}
    />;
  }
  if (saveOnBlur) {
    return <textarea
      value={local}
      onChange={e => setLocal(e.target.value)}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; if (local !== (value || '')) onChange(local); }}
      placeholder={placeholder}
      style={{ ...S.noteArea, minHeight }}
    />;
  }
  return <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ ...S.noteArea, minHeight }} />;
}

export function InlineInput({ value, onChange, onBlurSave, placeholder, style: extraStyle = {} }) {
  const [local, setLocal] = useState(value || '');
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setLocal(value || ''); }, [value]);
  if (onBlurSave) {
    return <input
      value={local}
      onChange={e => setLocal(e.target.value)}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; if (local !== (value || '')) onBlurSave(local); }}
      placeholder={placeholder}
      style={{ ...S.inputStyle, ...extraStyle }}
    />;
  }
  return <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ ...S.inputStyle, ...extraStyle }} />;
}

function formatVersionWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function versionWho(v) {
  return v.editedByName || 'Author not recorded';
}

// Hover text on a version chip — the date and who edited it. A renamed
// version keeps its position (v.02) in the tooltip.
function versionTooltip(v, canRename) {
  const name = v.isRenamed ? `${v.autoLabel} “${v.label}”` : v.label;
  return `${name} · ${formatVersionWhen(v.createdAt)} · ${versionWho(v)}${canRename ? '\nRight-click to rename' : ''}`;
}

// Header buttons keep one look — state is read from the glyph (a closed vs
// open padlock), not from the button's colour.
const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3,
  background: 'none', color: '#6E6C66', border: '1px solid rgba(27,27,25,.2)',
  borderRadius: 2, padding: '2px 5px', cursor: 'pointer', lineHeight: 1, flexShrink: 0,
};

// A text field that is locked (read-only) until the padlock in its top-right
// corner is clicked, with a strip of saved versions beside it. Locked is the
// default so a stray keystroke can't wipe wording that took work to write.
//
// Versions come from the parent (see useCommsFieldVersions): each entry is
// { id, label, autoLabel, isRenamed, content, editedByName, createdAt }.
// They are only ever created by the + button (onSaveVersion) — editing the
// field saves the text but does not add to its history. Hovering a chip
// shows the date and editor; clicking one opens a read-only preview that can
// be restored while the field is unlocked; right-clicking one renames it
// ("draft", "for publishing"…) via onRename.
export function LockableField({
  label,
  hint,
  value,
  onSave,
  onSaveVersion,
  onRestore,
  onRename,
  onDeleteVersion,
  actions,
  placeholder,
  multiline = true,
  minHeight = 120,
  canEdit = true,
  versions = [],
  compact = false,
  inputStyle = {},
}) {
  const [locked, setLocked] = useState(true);
  const [local, setLocal] = useState(value || '');
  const [openVersion, setOpenVersion] = useState(null);
  const [renaming, setRenaming] = useState(null); // { id, draft }
  const focused = useRef(false);

  useEffect(() => { if (!focused.current) setLocal(value || ''); }, [value]);
  // A locked field never holds unsaved edits — drop any stale draft.
  useEffect(() => { if (locked) { focused.current = false; setLocal(value || ''); } }, [locked, value]);

  const editable = canEdit && !locked;

  const commit = () => {
    focused.current = false;
    if (local !== (value || '')) onSave(local);
  };

  const base = multiline ? { ...S.noteArea, minHeight } : { ...S.inputStyle };
  const stateStyle = editable
    ? { backgroundColor: '#FFFDF4', borderColor: GOLD }
    : { backgroundColor: 'rgba(27,27,25,.03)', color: '#4A4844', cursor: 'default' };
  // stateStyle last: the locked/unlocked tint always wins over per-field styling.
  const fieldStyle = { ...base, ...inputStyle, ...stateStyle };

  const headerStyle = compact
    ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }
    : { ...S.blockTitle, marginBottom: 10 };

  const selected = openVersion && versions.find(v => v.id === openVersion);

  return (
    <div>
      <div style={headerStyle}>
        <span style={compact ? S.eyebrow : undefined}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', minWidth: 0 }}>
          {hint && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6E6C66', fontWeight: 400 }}>{hint}</span>}
          {versions.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, overflowX: 'auto', maxWidth: 220, scrollbarWidth: 'none' }}>
              {versions.map(v => {
                const active = openVersion === v.id;
                const canRename = canEdit && !!onRename;
                if (renaming && renaming.id === v.id) {
                  const currentName = v.isRenamed ? v.label : '';
                  const commitRename = () => {
                    if (renaming.draft.trim() !== currentName) onRename(v.id, renaming.draft);
                    setRenaming(null);
                  };
                  return (
                    <input
                      key={v.id}
                      autoFocus
                      value={renaming.draft}
                      onChange={e => setRenaming(r => ({ ...r, draft: e.target.value }))}
                      onBlur={commitRename}
                      onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') setRenaming(null);
                      }}
                      placeholder={v.autoLabel}
                      title="Name this version — empty resets it to its number"
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, width: 92, flexShrink: 0,
                        padding: '2px 4px', borderRadius: 2, border: '1px solid ' + GOLD, background: '#FFFDF4',
                        color: '#1B1B19', outline: 'none',
                      }}
                    />
                  );
                }
                return (
                  <button
                    key={v.id}
                    title={versionTooltip(v, canRename)}
                    onClick={() => setOpenVersion(active ? null : v.id)}
                    onContextMenu={e => {
                      if (!canRename) return;
                      e.preventDefault();
                      setRenaming({ id: v.id, draft: v.isRenamed ? v.label : '' });
                    }}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.04em',
                      padding: '2px 5px', borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer',
                      maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis',
                      border: '1px solid ' + (active ? GOLD : 'rgba(27,27,25,.2)'),
                      background: active ? GOLD : '#FBFAF7',
                      color: active ? '#F7F6F2' : '#6E6C66',
                    }}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
          )}
          {actions}
          {canEdit && onSaveVersion && (() => {
            // Always rendered, so every field carries the same controls — but
            // inert with nothing to snapshot or when the newest version
            // already holds this wording.
            const newest = versions[versions.length - 1];
            const empty = !local.trim();
            const duplicate = !empty && newest && newest.content === local;
            const inert = empty || duplicate;
            return (
              <button
                onClick={() => { if (!inert) onSaveVersion(local); }}
                title={empty ? 'Nothing to save as a version yet' : duplicate ? 'Already saved as the latest version' : 'Save the current text as a new version'}
                style={{ ...iconBtn, opacity: inert ? 0.35 : 1, cursor: inert ? 'default' : 'pointer' }}
              >
                <Plus size={11} />
              </button>
            );
          })()}
          {canEdit && (
            <button
              onClick={() => setLocked(l => !l)}
              title={locked ? 'Locked — click to unlock and edit' : 'Unlocked — click to lock'}
              style={iconBtn}
            >
              {locked ? <Lock size={12} /> : <Unlock size={12} />}
            </button>
          )}
        </div>
      </div>

      {selected && (
        <div style={{ border: '1px solid rgba(27,27,25,.12)', borderLeft: `2px solid ${GOLD}`, background: '#FBFAF7', borderRadius: 3, padding: '9px 12px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ ...S.eyebrow, color: GOLD }}>{selected.isRenamed ? `${selected.autoLabel} · ${selected.label}` : selected.label}</span>
            <span style={S.meta}>{formatVersionWhen(selected.createdAt)} · {versionWho(selected)}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              {canEdit && onRestore && (
                locked
                  ? <span style={{ ...S.meta, fontSize: 11 }}>Unlock to restore</span>
                  : selected.content !== (value || '') && (
                    <button
                      onClick={() => { onRestore(selected.content); setOpenVersion(null); }}
                      title="Replace the field with this version"
                      style={iconBtn}
                    >
                      <RotateCcw size={10} />
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Restore</span>
                    </button>
                  )
              )}
              {canEdit && onDeleteVersion && (
                <button
                  onClick={async () => { if (await onDeleteVersion(selected.id, selected.label) !== false) setOpenVersion(null); }}
                  title="Delete this version"
                  style={{ ...iconBtn, color: '#8A6A2A' }}
                >
                  <Trash2 size={11} />
                </button>
              )}
              <button onClick={() => setOpenVersion(null)} title="Close preview" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6E6C66', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
            </div>
          </div>
          <p style={{ fontSize: 13, color: '#1B1B19', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
            {selected.content || <span style={{ color: '#9A988F', fontStyle: 'italic' }}>(empty)</span>}
          </p>
        </div>
      )}

      {multiline ? (
        <textarea
          value={local}
          readOnly={!editable}
          onChange={e => setLocal(e.target.value)}
          onFocus={() => { focused.current = true; }}
          onBlur={commit}
          placeholder={editable ? placeholder : (value ? '' : placeholder)}
          style={fieldStyle}
        />
      ) : (
        <input
          value={local}
          readOnly={!editable}
          onChange={e => setLocal(e.target.value)}
          onFocus={() => { focused.current = true; }}
          onBlur={commit}
          placeholder={editable ? placeholder : (value ? '' : placeholder)}
          style={fieldStyle}
        />
      )}
    </div>
  );
}

export function LinkBadge({ value, onSave, placeholder = 'https://…', title: label }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value || '');
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setLocal(value || ''); }, [value, editing]);

  const save = () => {
    setEditing(false);
    setHovered(false);
    if (local !== (value || '')) onSave(local);
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1, minWidth: 0 }}>
        <Link2 size={12} style={{ color: '#9A988F', flexShrink: 0 }} />
        <input
          ref={inputRef}
          value={local}
          onChange={e => setLocal(e.target.value)}
          onBlur={save}
          onKeyDown={e => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') { setLocal(value || ''); setEditing(false); }
          }}
          placeholder={placeholder}
          autoFocus
          style={{ flex: 1, fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, background: 'transparent', border: 'none', borderBottom: '1px solid rgba(27,27,25,.18)', padding: '2px 0', outline: 'none', color: '#21302A', minWidth: 0 }}
        />
        {local && (
          <a href={local} target="_blank" rel="noreferrer" title="Open link" onMouseDown={e => e.preventDefault()} style={{ color: '#9A988F', display: 'flex', flexShrink: 0 }}>
            <ExternalLink size={11} />
          </a>
        )}
      </div>
    );
  }

  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
    >
      {value ? (
        <a href={value} target="_blank" rel="noreferrer" title={label ? `${label}: ${value}` : value} style={{ display: 'flex', color: GOLD }}>
          <ExternalLink size={13} />
        </a>
      ) : (
        <button onClick={() => setEditing(true)} title={label ? `Add ${label}` : 'Add link'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C8C4BB', padding: 0, display: 'flex' }}>
          <Link2 size={13} />
        </button>
      )}
      {hovered && (
        <button onClick={() => setEditing(true)} title="Edit link" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: '0 1px', display: 'flex', alignItems: 'center' }}>
          <Pencil size={9} />
        </button>
      )}
    </span>
  );
}

export function AddLine({ children }) {
  return <div style={S.addLine}>{children}</div>;
}

export function BtnGhost({ onClick, children, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'none', color: disabled ? '#8E9B95' : '#21302A', border: `1px solid ${disabled ? 'rgba(51,71,63,.4)' : '#33473F'}`, borderRadius: 3, padding: '8px 13px', cursor: disabled ? 'progress' : 'pointer', whiteSpace: 'nowrap' }}>
      {children}
    </button>
  );
}

export function BtnPrimary({ onClick, children, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', background: disabled ? '#8E9B95' : '#33473F', color: '#F7F6F2', border: 'none', borderRadius: 3, padding: '11px 18px', cursor: disabled ? 'progress' : 'pointer' }}>
      {children}
    </button>
  );
}

export function BackBtn({ onClick, label = 'Back' }) {
  return (
    <button onClick={onClick} style={{ background: 'none', border: 'none', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6E6C66', padding: 0, marginBottom: 20, cursor: 'pointer' }}>
      ← {label}
    </button>
  );
}

export function EmptyNote({ text }) {
  return <div style={{ color: '#9A988F', fontStyle: 'italic', fontSize: 13.5 }}>{text}</div>;
}

export function Toast({ message, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#21302A', color: '#F7F6F2', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, padding: '9px 16px', borderRadius: 4, zIndex: 60 }}>
      {message}
    </div>
  );
}

export function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(27,27,25,.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#EAE8E1', border: '1px solid rgba(27,27,25,.12)', borderRadius: 6, padding: 28, maxWidth: 380, width: '90vw' }}>
        <p style={{ fontSize: 15, color: '#1B1B19', marginBottom: 24 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <BtnGhost onClick={onCancel}>Cancel</BtnGhost>
          <button onClick={onConfirm} style={{ background: '#8A6A2A', color: '#F7F6F2', border: 'none', borderRadius: 3, padding: '8px 16px', cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

export function CommsModal({ title, onClose, children, width = 560 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(27,27,25,.45)', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 18px', overflowY: 'auto' }}>
      <div style={{ background: '#EAE8E1', border: '1px solid rgba(27,27,25,.12)', borderRadius: 6, padding: '26px 28px', maxWidth: width, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
          <h2 style={{ ...S.h2, fontSize: 24, marginTop: 4 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#6E6C66' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ ...S.eyebrow, marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );
}

export function CInput({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <Field label={label}>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={S.inputStyle} />
    </Field>
  );
}

export function CTextarea({ label, value, onChange, rows = 4, placeholder }) {
  return (
    <Field label={label}>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} style={{ ...S.noteArea, minHeight: rows * 22 }} />
    </Field>
  );
}

export function CSelect({ label, value, onChange, options }) {
  return (
    <Field label={label}>
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={S.inputStyle}>
        <option value="">— select —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

// Chips of linked project codes plus a select to add one. Same control as the
// outlet and project pickers on the publications cards, so awards and
// publications link to projects the same way. `projects` is any list of
// { project_code, name }; `codes` is the current text[] from the row.
export function ProjectCodesPicker({ codes, projects, canEdit, onSave, placeholder = 'PROJECT\u2026', title = 'Link project' }) {
  const linked = Array.isArray(codes) ? codes : [];
  const byCode = {};
  (projects || []).forEach(p => { if (p.project_code) byCode[p.project_code] = p; });
  const available = (projects || []).filter(p => p.project_code && !linked.includes(p.project_code));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {linked.map(code => (
        <span key={code} title={byCode[code]?.name || code} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', flexShrink: 0 }}>
          <span style={{ color: GOLD }}>{code}</span>
          {canEdit && <button onClick={() => onSave(linked.filter(c => c !== code))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C8C4BB', padding: '0 0 0 2px', lineHeight: 1, fontSize: 12 }} title="Unlink">×</button>}
        </span>
      ))}
      {canEdit && available.length > 0 && (
        <select
          value=""
          onChange={e => { if (e.target.value) onSave([...linked, e.target.value]); }}
          title={title}
          style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: linked.length === 0 ? '#C8C4BB' : '#9A988F', background: 'transparent', border: 'none', padding: '1px 0', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', width: linked.length === 0 ? 110 : 20, flexShrink: 0, letterSpacing: linked.length === 0 ? '0.08em' : 0 }}
        >
          <option value="">{linked.length === 0 ? placeholder : '+'}</option>
          {available.map(p => <option key={p.project_code} value={p.project_code}>{p.project_code} · {p.name}</option>)}
        </select>
      )}
    </div>
  );
}
